/**
 * Web Crawler Project Sample Data
 * Scalable web crawling system for e-commerce sites in Bangladesh with extensibility for news, stocks, and social media
 */

import type { Project, Task, Epic, Sprint, Priority, TaskStatus } from '../types';

const ORG_ID = 'org_intellesphere';
const PROJECT_ID = 'proj-web-crawler';

export const PROJECT: Project = {
  id: PROJECT_ID,
  organizationId: ORG_ID,
  name: 'VectorIndexer - Intelligent Web Crawler',
  description: 'Scalable distributed web crawling system for e-commerce deal detection with extensible architecture for news, stocks, and social media monitoring',
  status: 'Approved',
  proposal: `# VectorIndexer - Intelligent Web Crawler System

## Overview
Build a scalable, distributed web crawling system to monitor e-commerce websites in Bangladesh for deals and discounts. The system uses RabbitMQ for task distribution, 3 worker VMs on Tirzok Cloud, and features an extensible architecture for future expansion to news, stocks, and social media scraping.

## Key Objectives
- **Scalable Architecture**: Distributed worker system with RabbitMQ message queue
- **E-commerce Deal Detection**: Identify price drops, discounts, and special offers
- **Rate Limiting**: Intelligent crawling with 20-minute random intervals to avoid detection
- **Dynamic Content Handling**: Support for JavaScript-rendered pages, login/signup flows
- **Extensible Design**: Plugin architecture for newspapers, stock markets, and social media
- **Data Analysis**: Pattern detection, trend analysis, and deal scoring
- **Monitoring & Alerts**: Real-time system health monitoring and deal notifications

## Technology Stack
- **Worker VMs**: 3 VMs on Tirzok Cloud (2 CPU, 4GB RAM each)
- **Message Queue**: RabbitMQ for task distribution and coordination
- **Crawler Engine**: Python (Scrapy/Selenium) or Node.js (Puppeteer)
- **Database**: PostgreSQL for structured data, MongoDB for raw HTML/JSON
- **Cache**: Redis for session management and rate limiting
- **Storage**: MinIO/S3 for screenshots and archived pages
- **Monitoring**: Prometheus + Grafana for metrics
- **Orchestration**: Docker + Docker Compose

## Target E-commerce Sites (Phase 1)
1. **Daraz.com.bd** - Major marketplace
2. **Pickaboo.com** - Electronics and gadgets
3. **Ryans.com** - Computer hardware
4. **Shajgoj.com** - Beauty and cosmetics
5. **Ajkerdeal.com** - General marketplace
6. **Othoba.com** - Fashion and lifestyle
7. **Chaldal.com** - Groceries

## Architecture Components

### 1. Crawler Coordinator (Main Service)
- Task scheduling and distribution
- Site configuration management
- Worker health monitoring
- Deal detection logic

### 2. Worker Nodes (3 VMs)
- Consume tasks from RabbitMQ
- Execute crawling jobs
- Handle dynamic content (Selenium/Puppeteer)
- Upload results to storage

### 3. Rate Limiter
- Per-site rate limiting (20 min intervals)
- Random jitter to appear human-like
- Rotating user agents and proxies

### 4. Data Pipeline
- Extract product information
- Compare prices over time
- Detect deals and discounts
- Store in database

### 5. Plugin System (Extensibility)
- Abstract base crawler class
- Site-specific adapters
- Future plugins for:
  - News sites (Prothom Alo, Daily Star, etc.)
  - Stock exchanges (DSE, CSE)
  - Social media (Facebook, Twitter trending)

## Current Status
Project approved and scheduled to start next month. Infrastructure setup and initial architecture design in progress.

## Success Metrics
- Crawl success rate > 95%
- Deal detection accuracy > 90%
- Average crawl latency < 30 seconds
- Zero ban incidents from target sites
- System uptime > 99.5%`,
  costs: [
    { id: 'cost-wc-1', description: 'Tirzok Cloud VMs (3 x $50/month x 12 months)', amount: 1800, category: 'Infrastructure' },
    { id: 'cost-wc-2', description: 'Development Team (6 months)', amount: 180000, category: 'Personnel' },
    { id: 'cost-wc-3', description: 'Database & Storage (PostgreSQL + MongoDB + Redis)', amount: 3600, category: 'Infrastructure' },
    { id: 'cost-wc-4', description: 'Proxy Services (Residential proxies)', amount: 2400, category: 'Services' },
    { id: 'cost-wc-5', description: 'Monitoring & Alerts (Prometheus + Grafana Cloud)', amount: 1200, category: 'Services' },
  ],
  teamMembers: [
    { userId: 'hossainzarif2002@gmail.com', role: 'developer' },
    { userId: 'user-rafi', role: 'developer' },
    { userId: 'aidimentry', role: 'developer' },
    { userId: 'user-masud', role: 'stakeholder' },
    { userId: 'user-khaled', role: 'stakeholder' },
  ],
  createdAt: '2026-02-20T00:00:00Z',
  updatedAt: '2026-02-23T00:00:00Z',
  externalLinks: [
    { type: 'Documentation', url: 'https://docs.vectorindexer.io' },
    { type: 'RabbitMQ Admin', url: 'https://rabbitmq.vectorindexer.io' },
    { type: 'Grafana Dashboard', url: 'https://grafana.vectorindexer.io' },
  ],
  startDate: '2026-03-01',
  targetEndDate: '2026-08-31',
  costPerHour: 85,
};

// Epic definitions for the web crawler project
export const webCrawlerEpics: Epic[] = [
  {
    id: 'epic-wc-infra',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Infrastructure & Queue Setup',
    description: 'Set up Tirzok Cloud VMs, RabbitMQ message queue, Docker containers, and networking infrastructure',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 85,
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-wc-auth',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Authentication & Session Management',
    description: 'Handle website login/signup flows, session persistence, cookie management, and captcha solving',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 85,
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-wc-core',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Web Scraping Core Engine',
    description: 'Build the core crawling engine with support for static and dynamic content, HTML parsing, and data extraction',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 85,
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-wc-ratelimit',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Rate Limiting & Scheduling',
    description: 'Implement intelligent rate limiting with 20-minute intervals, random jitter, and distributed coordination',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 85,
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-wc-analysis',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Data Analysis & Deal Detection',
    description: 'Price comparison, discount detection, trend analysis, and deal scoring algorithms',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 85,
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-wc-storage',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Database & Storage',
    description: 'PostgreSQL schema design, MongoDB document structure, Redis caching, and S3 storage integration',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 85,
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-wc-workers',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Worker Management',
    description: 'Worker health checks, auto-scaling logic, task retry mechanisms, and failure handling',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 85,
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-wc-monitoring',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Monitoring & Alerting',
    description: 'Prometheus metrics, Grafana dashboards, alert rules, and notification system',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 85,
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-wc-extensibility',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Extensibility Framework',
    description: 'Plugin architecture, abstract base classes, and adapter pattern for future site types',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 85,
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-wc-ecommerce',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'E-commerce Site Adapters',
    description: 'Site-specific crawlers for Daraz, Pickaboo, Ryans, and other e-commerce platforms',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 85,
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-wc-future',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Future Adapters (News/Stocks/Social)',
    description: 'Extensible adapters for news sites, stock exchanges, and social media monitoring',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    costPerHour: 85,
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
];

// Tasks for the web crawler project
export const webCrawlerTasks: Task[] = [
  // Infrastructure & Queue Setup
  {
    id: 'WC-001',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Provision 3 VMs on Tirzok Cloud',
    description: 'Set up 3 worker VMs (2 CPU, 4GB RAM each) on Tirzok Cloud with Ubuntu 22.04 LTS',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Infrastructure',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-wc-infra',
    estimatedDays: 2,
    estimatedHours: 16,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [
      { type: 'Tirzok Cloud', url: 'https://cloud.tirzok.com' },
    ],
    changeLogs: [],
    startDate: '2026-03-01',
    targetEndDate: '2026-03-03',
  },
  {
    id: 'WC-002',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Install and configure RabbitMQ cluster',
    description: 'Set up RabbitMQ with high availability configuration, create queues for crawl tasks',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Infrastructure',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: ['WC-001'],
    subtaskIds: [],
    epicId: 'epic-wc-infra',
    estimatedDays: 3,
    estimatedHours: 24,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [
      { type: 'RabbitMQ Docs', url: 'https://www.rabbitmq.com/documentation.html' },
    ],
    changeLogs: [],
    dependencies: 'WC-001',
    startDate: '2026-03-03',
    targetEndDate: '2026-03-06',
  },
  {
    id: 'WC-003',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Set up Docker and Docker Compose on all VMs',
    description: 'Install Docker, configure networking, create docker-compose.yml for services',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Low',
    category: 'Infrastructure',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: ['WC-001'],
    subtaskIds: [],
    epicId: 'epic-wc-infra',
    estimatedDays: 1,
    estimatedHours: 8,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    dependencies: 'WC-001',
    startDate: '2026-03-03',
    targetEndDate: '2026-03-04',
  },
  {
    id: 'WC-004',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Configure VPC networking and security groups',
    description: 'Set up private network, configure firewall rules, enable inter-VM communication',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Infrastructure',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: ['WC-001'],
    subtaskIds: [],
    epicId: 'epic-wc-infra',
    estimatedDays: 2,
    estimatedHours: 16,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    dependencies: 'WC-001',
    startDate: '2026-03-03',
    targetEndDate: '2026-03-05',
  },
  {
    id: 'WC-005',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Set up PostgreSQL database',
    description: 'Install PostgreSQL, design schema for products, prices, deals, and crawl history',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Database',
    assigneeIds: ['user-rafi'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-wc-storage',
    estimatedDays: 3,
    estimatedHours: 24,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    startDate: '2026-03-01',
    targetEndDate: '2026-03-04',
  },
  {
    id: 'WC-006',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Set up MongoDB for raw data storage',
    description: 'Install MongoDB, design collections for raw HTML, JSON responses, and page snapshots',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'Medium',
    category: 'Database',
    assigneeIds: ['user-rafi'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-wc-storage',
    estimatedDays: 2,
    estimatedHours: 16,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    startDate: '2026-03-04',
    targetEndDate: '2026-03-06',
  },
  {
    id: 'WC-007',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Set up Redis for caching and rate limiting',
    description: 'Install Redis cluster, configure for session storage, rate limit counters, and worker coordination',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Low',
    category: 'Infrastructure',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-wc-storage',
    estimatedDays: 2,
    estimatedHours: 16,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    startDate: '2026-03-06',
    targetEndDate: '2026-03-08',
  },
  
  // Core Engine
  {
    id: 'WC-010',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Design abstract BaseCrawler class',
    description: 'Create extensible base class with common methods for all crawler types (e-commerce, news, stocks, social)',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: 'Core Engine',
    assigneeIds: ['user-sakib'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-wc-extensibility',
    estimatedDays: 4,
    estimatedHours: 32,
    notes: 'This is critical for extensibility. Must support plugins for news, stocks, social media in the future.',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    startDate: '2026-03-01',
    targetEndDate: '2026-03-05',
  },
  {
    id: 'WC-011',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Implement Puppeteer/Playwright for dynamic content',
    description: 'Set up headless browser automation for JavaScript-rendered pages',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Core Engine',
    assigneeIds: ['user-sakib'],
    parentTaskIds: ['WC-010'],
    subtaskIds: [],
    epicId: 'epic-wc-core',
    estimatedDays: 5,
    estimatedHours: 40,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    dependencies: 'WC-010',
    startDate: '2026-03-05',
    targetEndDate: '2026-03-10',
  },
  {
    id: 'WC-012',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Build HTML parsing and data extraction module',
    description: 'Use Cheerio/BeautifulSoup for parsing HTML, extracting product data with CSS selectors',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Core Engine',
    assigneeIds: ['user-rafi'],
    parentTaskIds: ['WC-010'],
    subtaskIds: [],
    epicId: 'epic-wc-core',
    estimatedDays: 4,
    estimatedHours: 32,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    dependencies: 'WC-010',
    startDate: '2026-03-05',
    targetEndDate: '2026-03-09',
  },
  {
    id: 'WC-013',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Implement rotating user agents and headers',
    description: 'Create pool of realistic user agents, randomize headers to avoid detection',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Low',
    category: 'Core Engine',
    assigneeIds: ['user-sakib'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-wc-core',
    estimatedDays: 2,
    estimatedHours: 16,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    startDate: '2026-03-10',
    targetEndDate: '2026-03-12',
  },
  {
    id: 'WC-014',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Build proxy rotation system',
    description: 'Integrate residential proxy service, implement rotation logic, handle proxy failures',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'Medium',
    category: 'Core Engine',
    assigneeIds: ['user-sakib'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-wc-core',
    estimatedDays: 3,
    estimatedHours: 24,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    startDate: '2026-03-12',
    targetEndDate: '2026-03-15',
  },

  // Rate Limiting
  {
    id: 'WC-020',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Implement distributed rate limiter with Redis',
    description: 'Build rate limiting system using Redis, ensure 20-minute minimum interval per site',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: 'Rate Limiting',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: ['WC-007'],
    subtaskIds: [],
    epicId: 'epic-wc-ratelimit',
    estimatedDays: 5,
    estimatedHours: 40,
    notes: 'Critical: Must prevent sending requests too frequently. 20-minute interval with random jitter.',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    dependencies: 'WC-007',
    startDate: '2026-03-08',
    targetEndDate: '2026-03-13',
  },
  {
    id: 'WC-021',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Add random jitter to crawl intervals',
    description: 'Implement randomization (±5 minutes) to make crawling pattern less predictable',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Low',
    category: 'Rate Limiting',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: ['WC-020'],
    subtaskIds: [],
    epicId: 'epic-wc-ratelimit',
    estimatedDays: 1,
    estimatedHours: 8,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    dependencies: 'WC-020',
    startDate: '2026-03-13',
    targetEndDate: '2026-03-14',
  },
  {
    id: 'WC-022',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Build task scheduler with priority queue',
    description: 'Create scheduler that distributes tasks to workers based on rate limits and priorities',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: 'Scheduling',
    assigneeIds: ['user-rafi'],
    parentTaskIds: ['WC-002', 'WC-020'],
    subtaskIds: [],
    epicId: 'epic-wc-ratelimit',
    estimatedDays: 5,
    estimatedHours: 40,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    dependencies: 'WC-002,WC-020',
    startDate: '2026-03-13',
    targetEndDate: '2026-03-18',
  },

  // Authentication
  {
    id: 'WC-030',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Build login automation module',
    description: 'Create module to handle automatic login to sites requiring authentication',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'High',
    category: 'Authentication',
    assigneeIds: ['user-sakib'],
    parentTaskIds: ['WC-011'],
    subtaskIds: [],
    epicId: 'epic-wc-auth',
    estimatedDays: 5,
    estimatedHours: 40,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    dependencies: 'WC-011',
    startDate: '2026-03-18',
    targetEndDate: '2026-03-23',
  },
  {
    id: 'WC-031',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Implement session and cookie management',
    description: 'Persist sessions in Redis, manage cookie expiration, handle session refresh',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'Medium',
    category: 'Authentication',
    assigneeIds: ['user-sakib'],
    parentTaskIds: ['WC-007', 'WC-030'],
    subtaskIds: [],
    epicId: 'epic-wc-auth',
    estimatedDays: 3,
    estimatedHours: 24,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    dependencies: 'WC-007,WC-030',
    startDate: '2026-03-23',
    targetEndDate: '2026-03-26',
  },
  {
    id: 'WC-032',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Integrate CAPTCHA solving service',
    description: 'Integrate 2Captcha or similar service for automatic CAPTCHA resolution',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Low',
    complexity: 'Medium',
    category: 'Authentication',
    assigneeIds: ['user-sakib'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-wc-auth',
    estimatedDays: 3,
    estimatedHours: 24,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    startDate: '2026-03-26',
    targetEndDate: '2026-03-29',
  },

  // Worker Management
  {
    id: 'WC-040',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Build worker health check system',
    description: 'Implement heartbeat mechanism, detect dead workers, redistribute tasks',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Worker Management',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: ['WC-002'],
    subtaskIds: [],
    epicId: 'epic-wc-workers',
    estimatedDays: 4,
    estimatedHours: 32,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    dependencies: 'WC-002',
    startDate: '2026-03-15',
    targetEndDate: '2026-03-19',
  },
  {
    id: 'WC-041',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Implement task retry mechanism',
    description: 'Build exponential backoff retry logic for failed crawl tasks',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Worker Management',
    assigneeIds: ['user-rafi'],
    parentTaskIds: ['WC-022'],
    subtaskIds: [],
    epicId: 'epic-wc-workers',
    estimatedDays: 3,
    estimatedHours: 24,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    dependencies: 'WC-022',
    startDate: '2026-03-18',
    targetEndDate: '2026-03-21',
  },
  {
    id: 'WC-042',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Build failure notification system',
    description: 'Send alerts when workers fail, crawls timeout, or rate limits are hit',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'Low',
    category: 'Worker Management',
    assigneeIds: ['user-rafi'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-wc-workers',
    estimatedDays: 2,
    estimatedHours: 16,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    startDate: '2026-03-21',
    targetEndDate: '2026-03-23',
  },

  // Data Analysis
  {
    id: 'WC-050',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Build price comparison engine',
    description: 'Compare current price with historical data, calculate percentage drops',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'Analysis',
    assigneeIds: ['user-rafi'],
    parentTaskIds: ['WC-005'],
    subtaskIds: [],
    epicId: 'epic-wc-analysis',
    estimatedDays: 4,
    estimatedHours: 32,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    dependencies: 'WC-005',
    startDate: '2026-03-25',
    targetEndDate: '2026-03-29',
  },
  {
    id: 'WC-051',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Implement discount detection algorithm',
    description: 'Identify flash sales, special offers, coupon codes, and bulk discounts',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: 'Analysis',
    assigneeIds: ['user-rafi'],
    parentTaskIds: ['WC-050'],
    subtaskIds: [],
    epicId: 'epic-wc-analysis',
    estimatedDays: 5,
    estimatedHours: 40,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    dependencies: 'WC-050',
    startDate: '2026-03-29',
    targetEndDate: '2026-04-03',
  },
  {
    id: 'WC-052',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Build deal scoring system',
    description: 'Create scoring algorithm based on discount percentage, product rating, demand',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'High',
    category: 'Analysis',
    assigneeIds: ['user-rafi'],
    parentTaskIds: ['WC-051'],
    subtaskIds: [],
    epicId: 'epic-wc-analysis',
    estimatedDays: 5,
    estimatedHours: 40,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    dependencies: 'WC-051',
    startDate: '2026-04-03',
    targetEndDate: '2026-04-08',
  },

  // Monitoring
  {
    id: 'WC-060',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Set up Prometheus for metrics collection',
    description: 'Install Prometheus, configure exporters for RabbitMQ, Redis, PostgreSQL',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'Medium',
    category: 'Monitoring',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-wc-monitoring',
    estimatedDays: 3,
    estimatedHours: 24,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    startDate: '2026-04-05',
    targetEndDate: '2026-04-08',
  },
  {
    id: 'WC-061',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Build Grafana dashboards',
    description: 'Create dashboards for crawl success rate, worker health, queue depth, deal trends',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'Low',
    category: 'Monitoring',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: ['WC-060'],
    subtaskIds: [],
    epicId: 'epic-wc-monitoring',
    estimatedDays: 3,
    estimatedHours: 24,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    dependencies: 'WC-060',
    startDate: '2026-04-08',
    targetEndDate: '2026-04-11',
  },
  {
    id: 'WC-062',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Configure alert rules',
    description: 'Set up alerts for worker failures, high queue depth, low success rate, database issues',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'Low',
    category: 'Monitoring',
    assigneeIds: ['hossainzarif2002@gmail.com'],
    parentTaskIds: ['WC-061'],
    subtaskIds: [],
    epicId: 'epic-wc-monitoring',
    estimatedDays: 2,
    estimatedHours: 16,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    dependencies: 'WC-061',
    startDate: '2026-04-11',
    targetEndDate: '2026-04-13',
  },

  // E-commerce Adapters
  {
    id: 'WC-070',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Build Daraz.com.bd crawler adapter',
    description: 'Site-specific adapter for Daraz - handle pagination, filters, product pages',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'High',
    category: 'E-commerce Adapter',
    assigneeIds: ['user-sakib'],
    parentTaskIds: ['WC-010', 'WC-011', 'WC-012'],
    subtaskIds: [],
    epicId: 'epic-wc-ecommerce',
    estimatedDays: 7,
    estimatedHours: 56,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [
      { type: 'Target Site', url: 'https://www.daraz.com.bd' },
    ],
    changeLogs: [],
    dependencies: 'WC-010,WC-011,WC-012',
    startDate: '2026-04-01',
    targetEndDate: '2026-04-08',
  },
  {
    id: 'WC-071',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Build Pickaboo.com crawler adapter',
    description: 'Site-specific adapter for Pickaboo electronics store',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'E-commerce Adapter',
    assigneeIds: ['user-sakib'],
    parentTaskIds: ['WC-070'],
    subtaskIds: [],
    epicId: 'epic-wc-ecommerce',
    estimatedDays: 5,
    estimatedHours: 40,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [
      { type: 'Target Site', url: 'https://www.pickaboo.com' },
    ],
    changeLogs: [],
    dependencies: 'WC-070',
    startDate: '2026-04-08',
    targetEndDate: '2026-04-13',
  },
  {
    id: 'WC-072',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Build Ryans.com crawler adapter',
    description: 'Site-specific adapter for Ryans computer hardware store',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Medium',
    category: 'E-commerce Adapter',
    assigneeIds: ['user-rafi'],
    parentTaskIds: ['WC-070'],
    subtaskIds: [],
    epicId: 'epic-wc-ecommerce',
    estimatedDays: 5,
    estimatedHours: 40,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [
      { type: 'Target Site', url: 'https://www.ryans.com' },
    ],
    changeLogs: [],
    dependencies: 'WC-070',
    startDate: '2026-04-08',
    targetEndDate: '2026-04-13',
  },
  {
    id: 'WC-073',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Build Ajkerdeal.com crawler adapter',
    description: 'Site-specific adapter for Ajkerdeal marketplace',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'Medium',
    category: 'E-commerce Adapter',
    assigneeIds: ['user-rafi'],
    parentTaskIds: ['WC-072'],
    subtaskIds: [],
    epicId: 'epic-wc-ecommerce',
    estimatedDays: 5,
    estimatedHours: 40,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [
      { type: 'Target Site', url: 'https://www.ajkerdeal.com' },
    ],
    changeLogs: [],
    dependencies: 'WC-072',
    startDate: '2026-04-13',
    targetEndDate: '2026-04-18',
  },

  // Future Extensibility
  {
    id: 'WC-080',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Design NewsAdapter base class',
    description: 'Create abstract class for news site crawling - article extraction, date parsing, category detection',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Low',
    complexity: 'Medium',
    category: 'Extensibility',
    assigneeIds: ['user-sakib'],
    parentTaskIds: ['WC-010'],
    subtaskIds: [],
    epicId: 'epic-wc-future',
    estimatedDays: 4,
    estimatedHours: 32,
    notes: 'Future use: Can be used for Prothom Alo, Daily Star, Bdnews24, etc.',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    dependencies: 'WC-010',
    startDate: '2026-05-01',
    targetEndDate: '2026-05-05',
  },
  {
    id: 'WC-081',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Design StockAdapter base class',
    description: 'Create abstract class for stock exchange crawling - price data, volume, market cap',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Low',
    complexity: 'Medium',
    category: 'Extensibility',
    assigneeIds: ['user-rafi'],
    parentTaskIds: ['WC-010'],
    subtaskIds: [],
    epicId: 'epic-wc-future',
    estimatedDays: 4,
    estimatedHours: 32,
    notes: 'Future use: Can be used for DSE (Dhaka Stock Exchange), CSE (Chittagong Stock Exchange)',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    dependencies: 'WC-010',
    startDate: '2026-05-05',
    targetEndDate: '2026-05-09',
  },
  {
    id: 'WC-082',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Design SocialMediaAdapter base class',
    description: 'Create abstract class for social media monitoring - posts, trends, engagement metrics',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Low',
    complexity: 'High',
    category: 'Extensibility',
    assigneeIds: ['user-sakib'],
    parentTaskIds: ['WC-010'],
    subtaskIds: [],
    epicId: 'epic-wc-future',
    estimatedDays: 5,
    estimatedHours: 40,
    notes: 'Future use: Can be used for Facebook pages, Twitter trending topics, LinkedIn posts',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    dependencies: 'WC-010',
    startDate: '2026-05-09',
    targetEndDate: '2026-05-14',
  },
  {
    id: 'WC-083',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Build plugin registry and loader system',
    description: 'Create system to dynamically load and register crawler adapters at runtime',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Low',
    complexity: 'Medium',
    category: 'Extensibility',
    assigneeIds: ['user-sakib'],
    parentTaskIds: ['WC-080', 'WC-081', 'WC-082'],
    subtaskIds: [],
    epicId: 'epic-wc-future',
    estimatedDays: 4,
    estimatedHours: 32,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-20T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
    dependencies: 'WC-080,WC-081,WC-082',
    startDate: '2026-05-14',
    targetEndDate: '2026-05-18',
  },
];

// Sprint 1: Infrastructure & Core Setup
export const webCrawlerSprint1: Sprint = {
  id: 'sprint-wc-infra-core',
  organizationId: ORG_ID,
  projectId: PROJECT_ID,
  name: 'Sprint 1: Infrastructure & Core Engine',
  startDate: '2026-03-01',
  endDate: '2026-03-15',
  status: 'Active',
  taskIds: [
    'WC-001', // Provision VMs
    'WC-002', // RabbitMQ
    'WC-003', // Docker
    'WC-004', // Networking
    'WC-005', // PostgreSQL
    'WC-006', // MongoDB
    'WC-007', // Redis
    'WC-010', // BaseCrawler design
    'WC-011', // Puppeteer/Playwright
    'WC-012', // HTML parsing
  ],
  goals: 'Set up all infrastructure components (VMs, RabbitMQ, databases) and build the core crawling engine with extensible architecture. By the end of this sprint, we should have a working infrastructure and a basic crawler that can extract data from static and dynamic pages.',
};

// Sprint 2: Rate Limiting & Worker Management
export const webCrawlerSprint2: Sprint = {
  id: 'sprint-wc-ratelimit-workers',
  organizationId: ORG_ID,
  projectId: PROJECT_ID,
  name: 'Sprint 2 - Rate Limiting & Worker Management',
  goal: 'Implement rate limiting system and enhance worker management',
  startDate: '2026-04-01',
  endDate: '2026-04-14',
  status: 'Active',
  taskIds: ['task-wc-ratelimiter-config', 'task-wc-ratelimiter-memory', 'task-wc-ratelimiter-redis', 'task-wc-worker-lifecycle', 'task-wc-worker-metrics'],
  plannedVelocity: 5,
  actualVelocity: 0,
  createdAt: '2026-02-15T00:00:00Z',
  updatedAt: '2026-02-15T00:00:00Z',
};

// Export aliases for consistency with other data files
export const webCrawlerProject = PROJECT;